/**
 * Hologram viewport — lit voxel shells (Three.js) + canvas fallback.
 * Stable per-palette InstancedMeshes; colorBlend lerps hologram → true color in place.
 */
import React, { useEffect, useRef, useState } from 'react';

/** Characters face −Z in our space; camera sits on +Z — start yaw so the front faces us. */
export const FRONT_YAW = Math.PI;

const HologramViewport = ({
  size = 16,
  voxels = [],
  palette = {},
  grayscale = true,
  colorBlend = 1,
  autoRotate = false,
  presentation = 'free', // 'free' | 'trophy'
  height = 260,
  /** Fly cubes in from off-screen, then settle into the model */
  assemble = false,
  assembleMs = 2400,
  /** Override initial yaw (radians). Default: front-facing. */
  initialYaw = FRONT_YAW,
  colorWave = false,
  /**
   * When true with colorBlend 0→1: popcorn palette pops — one true color
   * SNAP-appears at a time (largest first), cubes inflate on impact.
   */
  colorBurst = false,
}) => {
  const mountRef = useRef(null);
  const latestRef = useRef({
    voxels,
    palette,
    grayscale,
    colorBlend,
    autoRotate,
    presentation,
    assemble,
    assembleMs,
    initialYaw,
    colorWave,
    colorBurst,
  });
  latestRef.current = {
    voxels,
    palette,
    grayscale,
    colorBlend,
    autoRotate,
    presentation,
    assemble,
    assembleMs,
    initialYaw,
    colorWave,
    colorBurst,
  };
  const [mode, setMode] = useState('loading');

  useEffect(() => {
    let disposed = false;
    let cleanup = () => {};

    (async () => {
      try {
        const THREE = await import('three');
        if (disposed || !mountRef.current) return;
        cleanup = mountThree(THREE, mountRef.current, {
          size,
          height,
          ...latestRef.current,
        });
        flushPaint(mountRef.current, latestRef.current);
        setMode('three');
      } catch (e) {
        console.warn('[Hologram] Three.js unavailable, canvas fallback', e);
        if (disposed || !mountRef.current) return;
        cleanup = mountCanvas(mountRef.current, {
          size,
          height,
          ...latestRef.current,
        });
        flushPaint(mountRef.current, latestRef.current);
        setMode('canvas');
      }
    })();

    return () => {
      disposed = true;
      cleanup();
    };
  }, [size, height, presentation, assemble, initialYaw]);

  useEffect(() => {
    flushPaint(mountRef.current, {
      voxels,
      grayscale,
      palette,
      colorBlend,
      autoRotate,
      presentation,
      colorWave,
      colorBurst,
      size,
    });
  }, [voxels, grayscale, palette, colorBlend, autoRotate, presentation, colorWave, colorBurst, size]);

  return (
    <div
      ref={mountRef}
      data-mode={mode}
      style={{
        width: '100%',
        height,
        borderRadius: 12,
        border: '1px solid rgba(247, 240, 232, 0.08)',
        background: '#2a2420',
        touchAction: 'none',
        overflow: 'hidden',
      }}
    />
  );
};

function flushPaint(mount, L) {
  const api = mount?.__vqPaint;
  if (!api) return;
  api(
    L.voxels,
    L.grayscale,
    L.palette,
    L.autoRotate,
    L.colorBlend,
    L.colorWave,
    L.size,
    L.colorBurst
  );
}

function liftHex(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!m) return '#dce6f5';
  const n = parseInt(m[1], 16);
  let r = (n >> 16) & 255;
  let g = (n >> 8) & 255;
  let b = n & 255;
  const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  // MagicaVoxel often uses pure black fills; lift into readable dark fur tones
  // so Mesh materials don't collapse into a flat silhouette.
  if (luma < 18) {
    return '#4a4068';
  }
  if (luma < 40) {
    const boost = 48 - luma;
    r = Math.min(255, Math.round(r + boost * 1.15));
    g = Math.min(255, Math.round(g + boost * 1.1));
    b = Math.min(255, Math.round(b + boost * 1.35));
  }
  return '#' + [r, g, b].map((c) => c.toString(16).padStart(2, '0')).join('');
}

/** 0 at core … ~1 at corners — used for inside-out color bloom */
function radialDist(v, size) {
  const half = (size - 1) / 2 || 1;
  const dx = (v.x - half) / half;
  const dy = (v.y - half) / half;
  const dz = (v.z - half) / half;
  return Math.min(1, Math.sqrt(dx * dx + dy * dy + dz * dz) / Math.SQRT3);
}

/** Wave 0..1 → per-voxel blend (core colors first, rim last) */
function waveBlendForVoxel(v, size, wave) {
  const dist = radialDist(v, size);
  const spread = 0.7;
  return Math.min(1, Math.max(0, (wave - dist * spread) / Math.max(0.001, 1 - spread)));
}

function resolveColor(v, gray, pal, blend = 1, waveMode = false) {
  const raw =
    v.color ||
    pal?.[v.mat] ||
    pal?.[String(v.mat)] ||
    '#e8ddd2';
  const full = liftHex(raw);
  if (waveMode) {
    const holo = '#c9b39a';
    if (blend <= 0.001) return holo;
    if (blend >= 0.999) return full;
    return lerpHex(holo, full, blend);
  }
  if (gray) {
    const holo = '#c9b39a';
    // Uniform clay until reveal — no per-voxel luminance “texture”
    if (blend <= 0.001) return holo;
    if (blend >= 0.999) return full;
    return lerpHex(holo, full, blend);
  }
  if (blend >= 0.999) return full;
  if (blend <= 0.001) return '#c9b39a';
  return lerpHex('#c9b39a', full, blend);
}

function lerpHex(a, b, t) {
  const pa = parseInt(String(a).slice(1), 16);
  const pb = parseInt(String(b).slice(1), 16);
  const lr = (x) => (x >> 16) & 255;
  const lg = (x) => (x >> 8) & 255;
  const lb = (x) => x & 255;
  const r = Math.round(lr(pa) + (lr(pb) - lr(pa)) * t);
  const g = Math.round(lg(pa) + (lg(pb) - lg(pa)) * t);
  const bl = Math.round(lb(pa) + (lb(pb) - lb(pa)) * t);
  return '#' + [r, g, bl].map((c) => c.toString(16).padStart(2, '0')).join('');
}

function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}

function easeOutBack(t) {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
}

function hash01(x, y, z) {
  let h = (x * 73856093) ^ (y * 19349663) ^ (z * 83492791);
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b);
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

function mountThree(THREE, mount, opts) {
  const { size, height, presentation } = opts;
  const trophy = presentation === 'trophy';
  const width = mount.clientWidth || 320;
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
  renderer.setClearColor(0x2a2420, 1);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.setSize(width, height);
  if (THREE.SRGBColorSpace) renderer.outputColorSpace = THREE.SRGBColorSpace;
  mount.innerHTML = '';
  mount.appendChild(renderer.domElement);

  const scene = new THREE.Scene();
  const ambient = new THREE.AmbientLight(0xffe8d6, 0.78);
  scene.add(ambient);
  const keyLight = new THREE.DirectionalLight(0xfff1e0, 1.05);
  keyLight.position.set(size * 0.8, size * 1.2, size * 1.1);
  scene.add(keyLight);
  const fill = new THREE.DirectionalLight(0xe8a878, 0.42);
  fill.position.set(-size, size * 0.4, -size * 0.6);
  scene.add(fill);
  const rim = new THREE.DirectionalLight(0x6faf7a, 0.18);
  rim.position.set(0, -size * 0.2, -size);
  scene.add(rim);
  const camera = new THREE.PerspectiveCamera(42, width / height, 0.1, 200);
  const dist = size * 1.55;
  if (trophy) {
    // Elevated front three-quarter — model front faces camera (yaw = π)
    camera.position.set(dist * 0.35, dist * 0.38, dist * 1.45);
  } else {
    camera.position.set(dist * 0.45, dist * 0.55, dist * 1.55);
  }
  camera.lookAt(0, size * 0.02, 0);

  const geo = new THREE.BoxGeometry(1, 1, 1);
  const pivot = new THREE.Group();
  const startYaw = opts.initialYaw ?? FRONT_YAW;
  pivot.rotation.set(0, startYaw + (trophy ? 0.28 : 0.18), 0);
  scene.add(pivot);

  const buckets = new Map();
  const dummy = new THREE.Object3D();
  const half = (size - 1) / 2;
  const holoCol = new THREE.Color('#c9b39a');
  const tmpCol = new THREE.Color();
  let needsRender = true;
  let auto = opts.autoRotate;
  let blend = opts.colorBlend ?? 1;
  let grayMode = !!opts.grayscale;
  let burstMode = !!opts.colorBurst;
  let structureSig = '';
  /** @type {string[]} */
  let popOrder = [];

  // Assemble FX state
  let assembleActive = !!opts.assemble;
  const assembleMs = opts.assembleMs || 2400;
  const assembleT0 = performance.now();
  /** @type {{ mesh: any, index: number, tx:number, ty:number, tz:number, sx:number, sy:number, sz:number, delay:number, bucketKey:string }[]} */
  let parts = [];

  const clearBuckets = () => {
    for (const { mesh, mat } of buckets.values()) {
      pivot.remove(mesh);
      mesh.dispose();
      mat.dispose();
    }
    buckets.clear();
    parts = [];
    popOrder = [];
  };

  const getBucket = (trueHex, capacity) => {
    let b = buckets.get(trueHex);
    if (b && b.cap >= capacity) return b;
    if (b) {
      pivot.remove(b.mesh);
      b.mesh.dispose();
      b.mat.dispose();
      buckets.delete(trueHex);
    }
    const trueColor = new THREE.Color(trueHex);
    const mat = new THREE.MeshLambertMaterial({
      color: trueColor.clone(),
      emissive: 0x000000,
    });
    const mesh = new THREE.InstancedMesh(geo, mat, Math.max(capacity, 64));
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    mesh.count = 0;
    pivot.add(mesh);
    b = {
      mesh,
      mat,
      cap: Math.max(capacity, 64),
      trueHex,
      trueColor,
      popIndex: 0,
      count: 0,
    };
    buckets.set(trueHex, b);
    return b;
  };

  const writePart = (p, x, y, z, scale = 1) => {
    dummy.position.set(x, y, z);
    dummy.scale.setScalar(scale);
    dummy.updateMatrix();
    p.mesh.setMatrixAt(p.index, dummy.matrix);
  };

  const applyAssemble = (globalU) => {
    for (const p of parts) {
      const local = Math.min(1, Math.max(0, (globalU - p.delay) / (1 - p.delay * 0.85)));
      const e = easeOutBack(Math.min(1, local));
      writePart(
        p,
        p.sx + (p.tx - p.sx) * e,
        p.sy + (p.ty - p.sy) * e,
        p.sz + (p.tz - p.sz) * e,
        1
      );
    }
    for (const { mesh } of buckets.values()) {
      mesh.instanceMatrix.needsUpdate = true;
    }
  };

  const settleParts = (scaleForKey = null) => {
    for (const p of parts) {
      const s =
        scaleForKey && scaleForKey[p.bucketKey] != null
          ? scaleForKey[p.bucketKey]
          : 1;
      writePart(p, p.tx, p.ty, p.tz, s);
    }
    for (const { mesh } of buckets.values()) {
      mesh.instanceMatrix.needsUpdate = true;
    }
  };

  /** Popcorn snap — short, punchy arrival per color group */
  const easePop = (t) => {
    if (t <= 0) return 0;
    if (t >= 1) return 1;
    const c1 = 2.6;
    const c3 = c1 + 1;
    return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
  };

  /**
   * Popcorn palette reveal: one true color SNAP-pop at a time (big→small),
   * pops packed tight — pop-pop-pop — then full model.
   * Cubes in the active color inflate briefly as they ignite.
   */
  const applyBlendColors = (t) => {
    const u = Math.min(1, Math.max(0, Number.isFinite(t) ? t : 0));
    const scales = {};
    let ignition = 0;
    const n = Math.max(1, popOrder.length);

    if (u >= 0.999) {
      for (const b of buckets.values()) {
        if (grayMode) {
          // Hub clay hologram — never bake true palette while grayscale
          b.mat.color.copy(holoCol);
        } else {
          b.mat.color.copy(b.trueColor);
        }
        b.mat.emissive.setRGB(0, 0, 0);
        scales[b.trueHex] = 1;
      }
      keyLight.intensity = 1.15;
      ambient.intensity = 0.9;
      fill.intensity = 0.5;
      if (!assembleActive) settleParts(scales);
      return;
    }

    if (!burstMode || grayMode) {
      // Hub / non-ceremony: clay until blend rises (reveal uses burst path)
      for (const b of buckets.values()) {
        if (grayMode) {
          // Uniform clay — no per-material luminance “texture” before reveal
          b.mat.color.copy(holoCol);
          if (u > 0.001) {
            b.mat.color.lerp(b.trueColor, u);
          }
          b.mat.emissive.setRGB(0, 0, 0);
        } else if (u <= 0.001) {
          b.mat.color.copy(holoCol);
          b.mat.emissive.setRGB(0, 0, 0);
        } else {
          b.mat.color.copy(holoCol).lerp(b.trueColor, u);
          b.mat.emissive.setRGB(0, 0, 0);
        }
        scales[b.trueHex] = 1;
      }
      if (!assembleActive) settleParts(scales);
      return;
    }

    // Pack pops tightly across the timeline (overlapping slightly = popcorn)
    const window = Math.min(0.34, 0.9 / Math.max(2, n * 0.55));
    const gap = n <= 1 ? 0 : (0.92 - window) / (n - 1);

    for (const hex of popOrder) {
      const b = buckets.get(hex);
      if (!b) continue;
      const start = b.popIndex * gap;
      const local = Math.min(1, Math.max(0, (u - start) / window));

      if (local <= 0) {
        b.mat.color.copy(holoCol);
        b.mat.emissive.setRGB(0, 0, 0);
        scales[hex] = 1;
      } else if (local >= 1) {
        b.mat.color.copy(b.trueColor);
        b.mat.emissive.setRGB(0, 0, 0);
        scales[hex] = 1;
      } else {
        // Mid-pop: color snaps on + cube inflate/settle
        const snapped = easePop(local);
        const mix = Math.min(1, Math.max(0, snapped));
        b.mat.color.copy(holoCol).lerp(b.trueColor, mix);
        const flash = Math.sin(local * Math.PI);
        b.mat.emissive.copy(b.trueColor).multiplyScalar(flash * 0.7);
        const boost = 1 + flash * 0.55;
        b.mat.color.r = Math.min(1, b.mat.color.r * boost);
        b.mat.color.g = Math.min(1, b.mat.color.g * boost);
        b.mat.color.b = Math.min(1, b.mat.color.b * boost);
        // Scale punch: swell then ease back (popcorn kernel)
        scales[hex] = 1 + flash * 0.42;
        ignition = Math.max(ignition, flash);
      }
    }

    keyLight.intensity = 1.15 + ignition * 1.6;
    ambient.intensity = 0.9 + ignition * 0.4;
    fill.intensity = 0.5 + ignition * 0.35;

    if (!assembleActive) settleParts(scales);
  };

  const voxelSig = (visible, gray, burst) => {
    let s = `${visible.length}|g${gray ? 1 : 0}|b${burst ? 1 : 0}|pop|`;
    for (let i = 0; i < visible.length; i += Math.max(1, (visible.length / 24) | 0)) {
      const v = visible[i];
      s += `${v.x},${v.y},${v.z},${v.mat || v.color}|`;
    }
    return s;
  };

  const paint = (
    list,
    gray,
    pal,
    autoRot,
    colorBlend = 1,
    _waveUnused = false,
    _gridSize = size,
    nextBurst = false
  ) => {
    auto = !!autoRot && !assembleActive;
    blend = colorBlend;
    grayMode = !!gray;
    burstMode = !!nextBurst;
    const visible = (list || []).filter((v) => v.revealed);
    const sig = voxelSig(visible, grayMode, burstMode);

    if (sig !== structureSig) {
      clearBuckets();
      structureSig = sig;

      const grouped = new Map();
      for (const v of visible) {
        const raw =
          v.color ||
          pal?.[v.mat] ||
          pal?.[String(v.mat)] ||
          '#dce6f5';
        const trueHex = liftHex(raw);
        if (!grouped.has(trueHex)) grouped.set(trueHex, []);
        grouped.get(trueHex).push(v);
      }

      // Biggest color groups pop first (stronger “popcorn” beats)
      popOrder = [...grouped.entries()]
        .sort((a, b) => {
          const d = b[1].length - a[1].length;
          if (d !== 0) return d;
          return a[0].localeCompare(b[0]);
        })
        .map(([hex]) => hex);

      const scatterR = size * 2.4;
      popOrder.forEach((trueHex, popIndex) => {
        const verts = grouped.get(trueHex);
        const b = getBucket(trueHex, verts.length);
        b.popIndex = popIndex;
        b.count = verts.length;
        b.mesh.count = verts.length;
        verts.forEach((v, i) => {
          const tx = v.x - half;
          const ty = half - v.y;
          const tz = v.z - half;
          const h = hash01(v.x, v.y, v.z);
          const h2 = hash01(v.z, v.x, v.y);
          const h3 = hash01(v.y, v.z, v.x);
          const theta = h * Math.PI * 2;
          const phi = Math.acos(2 * h2 - 1);
          const rad = scatterR * (0.75 + h3 * 0.55);
          parts.push({
            mesh: b.mesh,
            index: i,
            bucketKey: trueHex,
            tx,
            ty,
            tz,
            sx: Math.sin(phi) * Math.cos(theta) * rad,
            sy: Math.cos(phi) * rad * 0.65 + size * 0.15,
            sz: Math.sin(phi) * Math.sin(theta) * rad,
            delay: h * 0.42,
          });
        });
        b.mesh.instanceMatrix.needsUpdate = true;
      });
    }

    applyBlendColors(blend);

    if (assembleActive) {
      const u = easeOutCubic(
        Math.min(1, (performance.now() - assembleT0) / assembleMs)
      );
      applyAssemble(u);
    }
    needsRender = true;
  };

  paint(
    opts.voxels,
    opts.grayscale,
    opts.palette,
    opts.autoRotate,
    opts.colorBlend ?? 1,
    !!opts.colorWave,
    size,
    !!opts.colorBurst
  );
  mount.__vqPaint = paint;

  let raf = 0;
  const loop = () => {
    raf = requestAnimationFrame(loop);
    // Re-apply blend every frame so color wash never stalls behind React
    if (buckets.size) applyBlendColors(blend);
    if (assembleActive) {
      const u = (performance.now() - assembleT0) / assembleMs;
      applyAssemble(easeOutCubic(Math.min(1, u)));
      pivot.rotation.y = startYaw + (trophy ? 0.28 : 0.18) + u * 0.35;
      if (u >= 1) {
        assembleActive = false;
        settleParts();
        auto = !!opts.autoRotate;
      }
      needsRender = true;
    } else if (auto) {
      pivot.rotation.y += trophy ? 0.008 : 0.012;
      if (trophy) {
        pivot.rotation.x = 0;
        pivot.rotation.z = 0;
      }
      needsRender = true;
    }
    if (needsRender || blend < 0.999) {
      renderer.render(scene, camera);
      needsRender = false;
    }
  };
  loop();

  let dragging = false;
  let lastX = 0;
  let lastY = 0;
  const onDown = (e) => {
    dragging = true;
    lastX = e.clientX;
    lastY = e.clientY;
  };
  const onMove = (e) => {
    if (!dragging) return;
    pivot.rotation.y += (e.clientX - lastX) * 0.01;
    if (!trophy) {
      pivot.rotation.x += (e.clientY - lastY) * 0.01;
    }
    lastX = e.clientX;
    lastY = e.clientY;
    needsRender = true;
  };
  const onUp = () => {
    dragging = false;
  };
  mount.addEventListener('pointerdown', onDown);
  window.addEventListener('pointermove', onMove);
  window.addEventListener('pointerup', onUp);

  return () => {
    cancelAnimationFrame(raf);
    mount.removeEventListener('pointerdown', onDown);
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', onUp);
    clearBuckets();
    geo.dispose();
    renderer.dispose();
    mount.innerHTML = '';
    delete mount.__vqPaint;
  };
}

function mountCanvas(mount, opts) {
  const { size, height } = opts;
  const canvas = document.createElement('canvas');
  const width = mount.clientWidth || 320;
  canvas.width = width;
  canvas.height = height;
  mount.innerHTML = '';
  mount.appendChild(canvas);
  const ctx = canvas.getContext('2d');
  let angle = opts.initialYaw ?? FRONT_YAW;
  let auto = opts.autoRotate;
  let list = opts.voxels || [];
  let gray = opts.grayscale;
  let pal = opts.palette;
  let blend = opts.colorBlend ?? 1;
  let assembleActive = !!opts.assemble;
  const assembleMs = opts.assembleMs || 2400;
  const assembleT0 = performance.now();
  let scatter = [];

  const rebuildScatter = () => {
    const half = (size - 1) / 2;
    const visible = (list || []).filter((v) => v.revealed);
    scatter = visible.map((v) => {
      const h = hash01(v.x, v.y, v.z);
      const h2 = hash01(v.z, v.x, v.y);
      return {
        v,
        tx: v.x - half,
        ty: half - v.y,
        tz: v.z - half,
        sx: (h - 0.5) * size * 4,
        sy: (h2 - 0.5) * size * 3,
        sz: (hash01(v.y, v.x, v.z) - 0.5) * size * 4,
        delay: h * 0.4,
      };
    });
  };

  let waveMode = !!opts.colorWave;
  const paint = (
    voxels,
    grayscale,
    palette,
    autoRot,
    colorBlend = 1,
    nextWave = false,
    _gridSize = size
  ) => {
    list = voxels || [];
    gray = grayscale;
    pal = palette;
    auto = !!autoRot && !assembleActive;
    blend = colorBlend;
    waveMode = !!nextWave;
    rebuildScatter();
    const u = assembleActive
      ? easeOutCubic(Math.min(1, (performance.now() - assembleT0) / assembleMs))
      : 1;
    draw(u);
  };
  mount.__vqPaint = paint;

  const draw = (u = 1) => {
    const g = ctx.createLinearGradient(0, 0, 0, height);
    g.addColorStop(0, '#322c28');
    g.addColorStop(1, '#2a2420');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, width, height);
    const cx = width / 2;
    const cy = height / 2;
    const scale = Math.min(width, height) / (size * 2.0);
    const items = scatter
      .map((p) => {
        const local = Math.min(1, Math.max(0, (u - p.delay) / 0.7));
        const e = easeOutCubic(local);
        return {
          ...p,
          x: p.sx + (p.tx - p.sx) * e,
          y: p.sy + (p.ty - p.sy) * e,
          z: p.sz + (p.tz - p.sz) * e,
        };
      })
      .sort((a, b) => a.x + a.z + a.y - (b.x + b.z + b.y));
    for (const p of items) {
      const rx = p.x * Math.cos(angle) - p.z * Math.sin(angle);
      const rz = p.x * Math.sin(angle) + p.z * Math.cos(angle);
      const sx = cx + (rx - rz) * scale;
      const sy = cy + ((rx + rz) * 0.35 - p.y) * scale;
      const vb = blend;
      ctx.fillStyle = resolveColor(p.v, gray, pal, vb, false);
      ctx.fillRect(sx, sy, Math.max(scale * 1.0, 2), Math.max(scale * 1.0, 2));
    }
  };

  let raf = 0;
  const loop = () => {
    raf = requestAnimationFrame(loop);
    if (assembleActive) {
      const u = (performance.now() - assembleT0) / assembleMs;
      draw(easeOutCubic(Math.min(1, u)));
      if (u >= 1) {
        assembleActive = false;
        auto = !!opts.autoRotate;
        draw(1);
      }
    } else if (auto) {
      angle += 0.01;
      draw(1);
    }
  };
  rebuildScatter();
  draw(assembleActive ? 0 : 1);
  loop();

  let dragging = false;
  let lastX = 0;
  const onDown = (e) => {
    dragging = true;
    lastX = e.clientX;
  };
  const onMove = (e) => {
    if (!dragging) return;
    angle += (e.clientX - lastX) * 0.01;
    lastX = e.clientX;
    draw(1);
  };
  const onUp = () => {
    dragging = false;
  };
  mount.addEventListener('pointerdown', onDown);
  window.addEventListener('pointermove', onMove);
  window.addEventListener('pointerup', onUp);

  return () => {
    cancelAnimationFrame(raf);
    mount.removeEventListener('pointerdown', onDown);
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', onUp);
    mount.innerHTML = '';
    delete mount.__vqPaint;
  };
}

export default HologramViewport;
